import React, { useState, useRef, useEffect } from "react";
import {
  ShareNetwork, Copy, EnvelopeSimple, Check,
  XLogo, FacebookLogo, WhatsappLogo, InstagramLogo, TiktokLogo,
  RedditLogo, TelegramLogo, LinkedinLogo, PinterestLogo, SnapchatLogo,
  DeviceMobile,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { slugify } from "../lib/slug";

function fillTemplate(template, vars) {
  return String(template || "").replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ""
  );
}

function buildShareUrl(shareCode, rank, title, artist) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  if (shareCode) return `${origin}/s/${shareCode}`;
  const slug = slugify(artist, title);
  return slug ? `${origin}/track/${rank}/${slug}` : `${origin}/track/${rank}`;
}

function ShareItem({ icon: Icon, label, onClick, href, testId, color }) {
  // Uses text-zinc-200 which the light-mode override in index.css darkens to #3f3f46,
  // so contrast is high in BOTH dark and light themes. The cyan-tinted hover bg makes
  // the selected item clearly visible regardless of theme.
  const common = "w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-zinc-200 hover:text-neon-cyan hover:bg-neon-cyan/15 focus:bg-neon-cyan/15 focus:text-neon-cyan focus:outline-none active:bg-neon-cyan/25 rounded transition-colors";
  if (href) {
    return (
      <a className={common} href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}>
        <Icon size={14} style={color ? { color } : undefined} />
        {label}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={common} data-testid={testId}>
      <Icon size={14} style={color ? { color } : undefined} />
      {label}
    </button>
  );
}

export default function ShareButton({ rank, title, artist, shareCode, slogan, sloganByPlatform, hashtags, buttonColor }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Pin title + artist + code at render time of the dropdown so the share
  // message never picks up a different song mid-click.
  const pinned = useRef({ rank, title, artist, shareCode });
  useEffect(() => { pinned.current = { rank, title, artist, shareCode }; }, [rank, title, artist, shareCode]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const url = buildShareUrl(pinned.current.shareCode, pinned.current.rank, pinned.current.title, pinned.current.artist);
  const defaultSlogan = slogan || "{song_title} by {artist_name} is Number {rank} on the Undaground Shoow Top Ten";
  const platformSlogans = sloganByPlatform || {};
  const buildText = (platform) => {
    const template = platformSlogans[platform] || defaultSlogan;
    return fillTemplate(template, {
      song_title: pinned.current.title,
      artist_name: pinned.current.artist,
      rank: pinned.current.rank,
    });
  };
  const tagSuffix = hashtags ? ` ${hashtags}` : "";
  const shareText = `${buildText("default")}${tagSuffix}`;
  const urlEnc = encodeURIComponent(url);

  // Per-platform pre-encoded texts (fall back to default slogan when platform-specific is unset).
  const xText = encodeURIComponent(`${buildText("x")}${tagSuffix}`);
  const redditText = encodeURIComponent(`${buildText("reddit")}${tagSuffix}`);
  const facebookText = encodeURIComponent(`${buildText("facebook")}${tagSuffix}`);
  const whatsappText = encodeURIComponent(`${buildText("whatsapp")}${tagSuffix} ${url}`);
  const genericText = encodeURIComponent(shareText);

  const copy = async (note) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success(note || "Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy automatically — long-press the link to copy");
    }
  };

  const copyForApp = (appName) => copy(`Link copied — paste it into your ${appName} caption, bio or DM.`);

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, url }); setOpen(false); } catch (_err) { void _err; /* user dismissed the system share sheet — no action needed */ }
    } else { copy(); }
  };

  const accent = buttonColor || "#00f0ff";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Share this track"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border-2 bg-ink-800/80 font-bold text-xs transition-all duration-300 shadow-[0_0_14px_rgba(0,240,255,0.25)] hover:shadow-[0_0_22px_rgba(0,240,255,0.5)]"
        style={{ color: accent, borderColor: accent }}
        data-testid={`share-button-${rank}`}
      >
        <ShareNetwork size={14} weight="bold" /> Share
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto glass rounded-lg border border-neon-cyan/30 p-2 z-30 scrollbar-thin shadow-xl"
          data-testid={`share-menu-${rank}`}
        >
          <p className="px-3 pt-1 pb-2 text-[10px] font-bold tracking-[0.25em] text-neon-cyan uppercase">Share #{String(rank).padStart(2, "0")}</p>

          <ShareItem icon={ShareNetwork} label="Share via system…" onClick={nativeShare} testId={`share-native-${rank}`} />
          <ShareItem
            icon={copied ? Check : Copy}
            label={copied ? "Copied" : "Copy link"}
            onClick={() => copy()}
            testId={`share-copy-${rank}`}
          />

          <div className="my-1.5 border-t border-neon-cyan/20" />

          {/* Direct share-URL platforms */}
          <ShareItem icon={XLogo}        label="X / Twitter" href={`https://twitter.com/intent/tweet?text=${xText}&url=${urlEnc}`} testId={`share-x-${rank}`} />
          <ShareItem icon={FacebookLogo} label="Facebook"    href={`https://www.facebook.com/sharer/sharer.php?u=${urlEnc}&quote=${facebookText}`} testId={`share-fb-${rank}`} color="#1877F2" />
          <ShareItem icon={WhatsappLogo} label="WhatsApp"    href={`https://wa.me/?text=${whatsappText}`} testId={`share-wa-${rank}`} color="#25D366" />
          <ShareItem icon={TelegramLogo} label="Telegram"    href={`https://t.me/share/url?url=${urlEnc}&text=${genericText}`} testId={`share-tg-${rank}`} color="#26A5E4" />
          <ShareItem icon={RedditLogo}   label="Reddit"      href={`https://www.reddit.com/submit?url=${urlEnc}&title=${redditText}`} testId={`share-reddit-${rank}`} color="#FF4500" />
          <ShareItem icon={LinkedinLogo} label="LinkedIn"    href={`https://www.linkedin.com/sharing/share-offsite/?url=${urlEnc}`} testId={`share-li-${rank}`} color="#0A66C2" />
          <ShareItem icon={PinterestLogo} label="Pinterest"  href={`https://pinterest.com/pin/create/button/?url=${urlEnc}&description=${genericText}`} testId={`share-pin-${rank}`} color="#E60023" />
          <ShareItem icon={SnapchatLogo} label="Snapchat"    href={`https://www.snapchat.com/scan?attachmentUrl=${urlEnc}`} testId={`share-snap-${rank}`} color="#FFFC00" />
          <ShareItem icon={EnvelopeSimple} label="Email"     href={`mailto:?subject=${encodeURIComponent("Check out this track")}&body=${encodeURIComponent(shareText + "\n\n" + url)}`} testId={`share-email-${rank}`} />
          <ShareItem icon={DeviceMobile} label="SMS (mobile)" href={`sms:?&body=${encodeURIComponent(shareText + " " + url)}`} testId={`share-sms-${rank}`} />

          <div className="my-1.5 border-t border-neon-cyan/20" />
          <p className="px-3 py-1 text-[10px] text-zinc-400 font-medium">Copy + paste apps (no public share URL):</p>
          <ShareItem icon={InstagramLogo} label="Instagram (copy)" onClick={() => copyForApp("Instagram")} testId={`share-ig-${rank}`} color="#E1306C" />
          <ShareItem icon={TiktokLogo}    label="TikTok (copy)"    onClick={() => copyForApp("TikTok")}    testId={`share-tt-${rank}`} />
          <ShareItem icon={DeviceMobile}  label="Kik (copy)"       onClick={() => copyForApp("Kik")}       testId={`share-kik-${rank}`} />
        </div>
      )}
    </div>
  );
}
